/**
 * views/CustomerDashboard.tsx — /dashboard/inventory
 * ────────────────────────────────────────────────────
 * AppSheet-replacement inventory audit dashboard for warehouse customers.
 *
 * Layout
 * ──────
 * ┌───────────────────────────────────────────────────────────────┐
 * │  HEADER  Mission picker · Summary chips · Export · User       │
 * ├─────────────────────────────┬─────────────────────────────────┤
 * │  LEFT PANE                  │  RIGHT PANE                     │
 * │  LocationList               │  LocationDetail                 │
 * │  Search + filter bar        │  Photo + comparison ledger      │
 * │  Scrollable card list       │  Lot / expiry / confidence      │
 * └─────────────────────────────┴─────────────────────────────────┘
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { fetchMissions, fetchMissionRecords, buildPhotoThumbUrl } from '@/api/inventory';
import { QRCorrectionWorklist } from './QRCorrectionWorklist';
import { SkuWorklist } from './SkuWorklist';
import { useAuthImage } from '@/hooks/useAuthImage';
import { apiClient } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { LocationDetail } from '@/components/dashboard/LocationDetail';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { reportProfileFor } from '@/lib/reportProfile';
import { Spinner } from '@/components/ui/Spinner';
import { InfiniumMark } from '@/components/ui/InfiniumLogo';

import type {
  LocationNode,
  MissionReconciliationData,
  MissionSummary,
  ReconciliationFlag,
  ReconciliationRecord,
} from '@/types';

function aisleGroup(value: string | null | undefined): string {
  const raw = (value ?? '').trim().toUpperCase();
  const aisle = raw.split(/[-_\s]+/)[0] ?? '';
  return aisle === 'RTD' || aisle === 'RTF' ? 'RTF/RTD' : aisle;
}

// ── Summary chips ─────────────────────────────────────────────────────────────

interface SummaryChipProps {
  label: string;
  count: number;
  variant: 'match' | 'warn' | 'danger' | 'purple' | 'neutral';
  active?: boolean;
}

function SummaryChip({ label, count, variant, active }: SummaryChipProps) {
  return (
    <div className={`sum-chip sum-chip--${variant}${active ? ' sum-chip--active' : ''}`}>
      <span className="sum-chip-count">{count}</span>
      <span className="sum-chip-label">{label}</span>
    </div>
  );
}

/**
 * Top-header result chips — exactly three, counted from the persisted
 * `customer_result` projection (Match | Mismatch | N/A). No detection-stage
 * flags (Count Off / SKU Mismatch / Review) are surfaced here.
 */
function MissionSummaryBar({ records }: { records: ReconciliationRecord[] }) {
  const counts = useMemo(() => {
    // 'Needs Review' = N/A with a photo captured but not confidently attributed
    // (lone slot-QR / conflict). 'No Photo' = genuinely no photo. Both tallied
    // as their own buckets, NOT under N/A, so the header matches the list.
    const c = { Match: 0, Mismatch: 0, 'N/A': 0, NeedsReview: 0, NoPhoto: 0 };
    for (const r of records) {
      if (r.needs_review) { c.NeedsReview += 1; continue; }
      if (r.missing_photo) { c.NoPhoto += 1; continue; }
      c[r.customer_result] = (c[r.customer_result] ?? 0) + 1;
    }
    return c;
  }, [records]);

  return (
    <div className="summary-bar">
      <SummaryChip label="Match"    count={counts.Match}    variant="match"  />
      <SummaryChip label="Mismatch" count={counts.Mismatch} variant="danger" />
      <SummaryChip label="N/A"      count={counts['N/A']}   variant="warn"   />
      {counts.NeedsReview > 0 && (
        <SummaryChip label="Needs Review" count={counts.NeedsReview} variant="neutral" />
      )}
      {counts.NoPhoto > 0 && (
        <SummaryChip label="No Photo" count={counts.NoPhoto} variant="neutral" />
      )}
    </div>
  );
}

// ── Result donut (Match / Mismatch / N/A) ──────────────────────────────────────

const RESULT_SEGMENTS: { key: 'Match' | 'Mismatch' | 'N/A' | 'Needs Review' | 'No Photo'; color: string }[] = [
  { key: 'Match',        color: '#22c55e' },
  { key: 'Mismatch',     color: '#ef4444' },
  { key: 'N/A',          color: '#f59e0b' },
  { key: 'Needs Review', color: '#8b5cf6' },
  { key: 'No Photo',     color: '#6b7280' },
];

function ResultDonut({ records }: { records: ReconciliationRecord[] }) {
  const { counts, total } = useMemo(() => {
    const c: Record<string, number> = {
      Match: 0, Mismatch: 0, 'N/A': 0, 'Needs Review': 0, 'No Photo': 0,
    };
    for (const r of records) {
      // 'Needs Review' (photo captured, unattributed) and 'No Photo' (none) are
      // their own buckets, not N/A, so the donut/header match the list filters.
      if (r.needs_review) { c['Needs Review'] += 1; continue; }
      if (r.missing_photo) { c['No Photo'] += 1; continue; }
      const key = r.customer_result ?? 'N/A';
      c[key] = (c[key] ?? 0) + 1;
    }
    return { counts: c, total: records.length };
  }, [records]);

  if (total === 0) return null;

  // Percentages are computed over the AUDITED base: Match / (Match + Mismatch).
  // N/A is EXCLUDED from the denominator. USER-MANDATED (2026-07-21, superseding the
  // brief all-locations experiment) — do NOT add N/A back into the denominator; the
  // headline % is inventory accuracy over audited locations only. Kept in lockstep
  // with the Excel export's "Inventory Accuracy (Excluding NA)". See memory
  // 'donut-match-rate-all-locations'.
  const scored = counts.Match + counts.Mismatch;
  const pct = (n: number) => (scored > 0 ? ((n / scored) * 100).toFixed(1) : '0.0');

  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="result-donut">
      <svg width={104} height={104} viewBox="0 0 104 104" role="img" aria-label="Result distribution">
        <g transform="rotate(-90 52 52)">
          <circle cx={52} cy={52} r={radius} fill="none" stroke="#e5e7eb" strokeWidth={14} />
          {RESULT_SEGMENTS.filter(s => counts[s.key] > 0).map((s) => {
            const len = (counts[s.key] / total) * circumference;
            const dash = (
              <circle
                key={s.key}
                cx={52} cy={52} r={radius} fill="none"
                stroke={s.color} strokeWidth={14}
                strokeDasharray={`${len} ${circumference - len}`}
                strokeDashoffset={-offset}
              />
            );
            offset += len;
            return dash;
          })}
        </g>
        <text x={52} y={49} textAnchor="middle" fontSize={16} fontWeight={700} fill="#f8fafc">
          {pct(counts.Match)}%
        </text>
        <text x={52} y={64} textAnchor="middle" fontSize={9} fill="#cbd5e1">Match</text>
      </svg>
      <div className="result-donut-legend">
        {RESULT_SEGMENTS.map((s) => (
          <span key={s.key} className="result-donut-legend-item">
            <span className="result-donut-swatch" style={{ background: s.color }} />
            {s.key}<strong>{counts[s.key]}</strong>
            {/* N/A and No Photo are excluded from the accuracy denominator → count only. */}
            {s.key !== 'N/A' && s.key !== 'No Photo' && (
              <span className="result-donut-pct">{pct(counts[s.key])}%</span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Discrepancy analysis (manager / admin / super_admin) ───────────────────────
// Auto-generated from the persisted customer_result + comments on every report
// load: categorises the Mismatch locations and writes a plain-English narrative
// the manager can relay to the customer. No backend call — derived from the same
// flat_records the donut uses, so it always matches the report.
const MISMATCH_CATEGORIES: {
  key: string;
  label: string;
  color: string;
  test: (comment: string) => boolean;
  blurb: (n: number) => string;
}[] = [
  {
    key: 'empty',
    label: 'Empty — WMS expects stock',
    color: '#f59e0b',
    test: c => /detected no pallet|non-?empty pallet/i.test(c),
    blurb: n =>
      `${n} ${n === 1 ? 'location was' : 'locations were'} found empty by the drone while the WMS still shows stock — typically picks or moves not yet posted, or pallets relocated without a system update. Walk these first.`,
  },
  {
    key: 'master',
    label: 'Master-data error (SKU master)',
    color: '#a855f7',
    test: c => /likely error in the master sku/i.test(c),
    blurb: n =>
      `${n} ${n === 1 ? 'location involves a SKU' : 'locations involve SKUs'} whose master-file layer configuration looks incorrect (counted per-unit instead of per-carton), so the expected quantity cannot reconcile. Correcting the SKU master clears ${n === 1 ? 'it' : 'them'}.`,
  },
  {
    key: 'wrong',
    label: 'Wrong pallet (misplacement)',
    color: '#ef4444',
    test: c => /does not match the wms sku/i.test(c),
    blurb: n =>
      `${n} ${n === 1 ? 'location holds' : 'locations hold'} a different pallet than the WMS expects — genuine misplacements to be put away to the correct slot.`,
  },
  {
    key: 'count',
    label: 'Count discrepancy',
    color: '#3b82f6',
    test: c => /count mismatch/i.test(c),
    blurb: n =>
      `${n} ${n === 1 ? 'location has' : 'locations have'} the correct pallet but a carton count differing from the WMS by more than one layer — a quantity variance to recount.`,
  },
];

function ExecutiveSummary({
  records,
  missionTitle,
  missionId,
  onBack,
}: {
  records: ReconciliationRecord[];
  missionTitle: string;
  missionId: string;
  onBack: () => void;
}) {
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const generateAi = useCallback(async () => {
    if (!missionId) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const res = await apiClient.post<{ summary: string }>(
        `/api/inventory/missions/${missionId}/ai-summary`,
        {},
      );
      setAiSummary(res.summary);
    } catch {
      setAiError('Could not generate the AI summary. If this keeps happening, the server may be missing the AI API key.');
    } finally {
      setAiLoading(false);
    }
  }, [missionId]);

  // Auto-generate the AI narrative when the panel first opens.
  useEffect(() => { generateAi(); }, [generateAi]);

  const { total, mismatch, cats, other, matchRate } = useMemo(() => {
    const total = records.length;
    const mm = records.filter(r => r.customer_result === 'Mismatch');
    const cats = MISMATCH_CATEGORIES.map(cat => ({
      ...cat,
      count: mm.filter(r => cat.test(r.comments ?? '')).length,
    }));
    const categorised = cats.reduce((s, c) => s + c.count, 0);
    return {
      total,
      mismatch: mm.length,
      cats,
      other: mm.length - categorised,
      matchRate: total ? Math.round(((total - mm.length) / total) * 100) : 0,
    };
  }, [records]);

  const present = cats.filter(c => c.count > 0);
  const share = (n: number) => (mismatch ? Math.round((n / mismatch) * 100) : 0);

  return (
    <div className="exec-summary">
      <div className="exec-summary-bar">
        <button className="btn btn--ghost btn--sm" onClick={onBack}>← Back to report</button>
        <div className="exec-summary-titles">
          <h1>Executive Summary</h1>
          {missionTitle && <p>{missionTitle}</p>}
        </div>
      </div>

      <div className="exec-summary-body">
        <section className="exec-overview">
          <div className="exec-overview-donut"><ResultDonut records={records} /></div>
          <div className="exec-overview-headline">
            <span className="exec-headline-rate">{matchRate}%</span>
            <p>
              <strong>{total - mismatch}</strong> of <strong>{total}</strong> locations reconciled.{' '}
              {mismatch > 0
                ? <><strong>{mismatch}</strong> require attention.</>
                : 'No discrepancies found. ✓'}
            </p>
          </div>
        </section>

        {mismatch > 0 && (
          <>
            <h2 className="exec-section-title">Discrepancy breakdown</h2>
            <table className="exec-table">
              <thead>
                <tr><th>Category</th><th className="num">Count</th><th className="num">Share</th></tr>
              </thead>
              <tbody>
                {present.map(c => (
                  <tr key={c.key}>
                    <td><span className="exec-cat-dot" style={{ background: c.color }} />{c.label}</td>
                    <td className="num">{c.count}</td>
                    <td className="num">{share(c.count)}%</td>
                  </tr>
                ))}
                {other > 0 && (
                  <tr>
                    <td><span className="exec-cat-dot" style={{ background: '#94a3b8' }} />Other (manual review)</td>
                    <td className="num">{other}</td>
                    <td className="num">{share(other)}%</td>
                  </tr>
                )}
                <tr className="exec-total-row">
                  <td>Total to review</td><td className="num">{mismatch}</td><td className="num">100%</td>
                </tr>
              </tbody>
            </table>

            <h2 className="exec-section-title">What went wrong — summary for the customer</h2>
            <p className="exec-narrative">
              This stocktake reconciled <strong>{total - mismatch}</strong> of <strong>{total}</strong> locations
              (≈{matchRate}%); <strong>{mismatch}</strong> require attention.{' '}
              {present.map(c => c.blurb(c.count)).join(' ')}
              {other > 0 && ` ${other} further ${other === 1 ? 'location' : 'locations'} fall outside these groups and need manual review.`}
            </p>
          </>
        )}

        <div className="exec-ai">
          <div className="exec-ai-head">
            <h2 className="exec-section-title">AI-assisted summary</h2>
            <button
              className="btn btn--ghost btn--sm"
              onClick={generateAi}
              disabled={aiLoading || !missionId}
            >
              {aiLoading ? '⏳ Generating…' : aiSummary ? '↻ Regenerate' : '✨ Generate AI summary'}
            </button>
          </div>
          {aiError && <p className="exec-ai-error">{aiError}</p>}
          {aiSummary ? (
            <p className="exec-narrative exec-ai-body">{aiSummary}</p>
          ) : !aiLoading && (
            <p className="exec-ai-hint">
              Generate a fresh, AI-written narrative of these results. The phrasing may vary
              each time you regenerate.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Mission picker ────────────────────────────────────────────────────────────

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

interface MissionPickerProps {
  missions: MissionSummary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function MissionPicker({ missions, selectedId, onSelect }: MissionPickerProps) {
  // Group missions by warehouse so cross-warehouse viewers (admins) can tell
  // sites apart. Single-warehouse users see the flat list as before.
  const groups = useMemo(() => {
    const map = new Map<string, MissionSummary[]>();
    for (const m of missions) {
      const key = m.warehouse_name ?? 'Unknown warehouse';
      const list = map.get(key) ?? [];
      list.push(m);
      map.set(key, list);
    }
    return Array.from(map.entries());
  }, [missions]);

  const renderOption = (m: MissionSummary) => (
    <option key={m.mission_id} value={m.mission_id}>
      {m.mission_title ?? `Mission ${m.mission_id.slice(0, 8)}…`}
      {' · '}
      {formatDate(m.last_updated)}
      {' · '}
      {m.record_count} locations
    </option>
  );

  return (
    <div className="mission-picker-wrap">
      <label className="mission-picker-label" htmlFor="mission-select">
        Audit Mission
      </label>
      <select
        id="mission-select"
        className="mission-picker-select"
        value={selectedId ?? ''}
        onChange={(e) => onSelect(e.target.value)}
      >
        <option value="" disabled>Select a mission…</option>
        {groups.length > 1
          ? groups.map(([wh, list]) => (
              <optgroup key={wh} label={wh}>{list.map(renderOption)}</optgroup>
            ))
          : missions.map(renderOption)}
      </select>
    </div>
  );
}

// ── Location card (left-pane list item) ───────────────────────────────────────

const FLAG_BORDER: Record<string, string> = {
  MATCH:             'loc-card--match',
  DISCREPANCY_COUNT: 'loc-card--discrepancy',
  MISMATCH_SKU:      'loc-card--mismatch',
  EMPTY_SLOT:        'loc-card--empty',
  DETECTION_FAILURE: 'loc-card--failure',
};

interface LocationCardProps {
  record: ReconciliationRecord;
  selected: boolean;
  onClick: () => void;
}

function LocationCard({ record, selected, onClick }: LocationCardProps) {
  // The card tag must ALWAYS agree with the detail pane's Result (top-right),
  // which renders customer_result — so derive the badge from customer_result
  // itself, never from a re-derivation. (An effective-flag mapping almost
  // agrees, but e.g. an EMPTY_SLOT override reads "Match" in the pane while
  // badging "Empty" on the card.) MISMATCH_SKU keeps its richer label since
  // it is a Mismatch either way.
  const effectiveFlag = (record.override_flag as ReconciliationFlag | null) ?? record.flag;
  const RESULT_TO_FLAG: Record<string, ReconciliationFlag> = {
    Match: 'MATCH',
    Mismatch: 'DISCREPANCY_COUNT',
    'N/A': 'DETECTION_FAILURE',
  };
  const badgeFlag: ReconciliationFlag =
    record.customer_result === 'Mismatch' && effectiveFlag === 'MISMATCH_SKU'
      ? 'MISMATCH_SKU'
      : RESULT_TO_FLAG[record.customer_result] ?? 'DETECTION_FAILURE';
  const borderClass = record.needs_review
    ? 'loc-card--review'
    : record.missing_photo ? 'loc-card--no-photo' : (FLAG_BORDER[badgeFlag] ?? '');
  const delta = record.discrepancy_delta;
  const deltaStr = delta == null ? '' : delta === 0 ? '±0' : `${delta > 0 ? '+' : ''}${delta}`;

  // Lazy-load thumbnail only when the card enters the viewport.
  // This limits concurrent requests to ~8-10 visible cards instead of all 120 at once.
  const cardRef = useRef<HTMLButtonElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { rootMargin: '120px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const thumbSrc = useAuthImage(inView && record.photo_id ? buildPhotoThumbUrl(record.photo_id) : null);

  return (
    <button
      ref={cardRef}
      type="button"
      className={`loc-card ${borderClass}${selected ? ' loc-card--selected' : ''}`}
      onClick={onClick}
      aria-pressed={selected}
    >
      {/* Thumbnail — fetched only when card is visible; plain <img> can't send JWT */}
      <div className="loc-card-thumb">
        {typeof thumbSrc === 'string' ? (
          <img src={thumbSrc} alt={record.location_code} />
        ) : (
          <span className="loc-card-thumb-placeholder">📷</span>
        )}
      </div>

      {/* Body */}
      <div className="loc-card-body">
        <div className="loc-card-top">
          <span className="loc-card-code">{record.location_code}</span>
          {record.needs_review ? (
            <span className="badge badge--review badge--sm">Needs Review</span>
          ) : record.missing_photo ? (
            <span className="badge badge--no-photo badge--sm">No Photo</span>
          ) : (
            <StatusBadge flag={badgeFlag} size="sm" />
          )}
        </div>
        {record.expected_sku && record.expected_sku !== 'UNKNOWN' && (
          <div className="loc-card-sku">{record.expected_sku}</div>
        )}
        <div className="loc-card-qty">
          <span className="loc-card-qty-exp">Exp {record.expected_boxes ?? '—'}</span>
          <span className="loc-card-qty-arrow">→</span>
          <span className="loc-card-qty-det">Det {record.calculated_boxes ?? '—'}</span>
          {deltaStr && (
            <span className={`loc-card-delta${delta !== 0 ? ' loc-card-delta--off' : ''}`}>
              {deltaStr}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// ── Location list (searchable, filterable) ────────────────────────────────────

// Customer-facing result filter. Match/Mismatch/N/A key off the persisted
// `customer_result` projection. "Empty" is cross-cutting — physically empty
// locations (drone_count === 0) regardless of result, so customers can find
// empty slots at a glance.
const RESULT_OPTIONS: { value: string; label: string }[] = [
  { value: 'ALL',      label: 'All'        },
  { value: 'Match',    label: '✓ Match'    },
  { value: 'Mismatch', label: '✗ Mismatch' },
  { value: 'N/A',      label: '◐ N/A'      },
  { value: 'Empty',    label: '○ Empty'    },
  { value: 'MissingQR', label: '◌ Missing QR' },
];

interface LocationListProps {
  flatRecords: ReconciliationRecord[];
  selectedCode: string | null;
  onSelect: (loc: LocationNode) => void;
}

function LocationList({ flatRecords, selectedCode, onSelect }: LocationListProps) {
  const [search, setSearch]     = useState('');
  const [searchComment, setSearchComment] = useState('');
  const [filterFlag, setFilter] = useState('ALL');
  const [filterAisle, setAisle] = useState('ALL');

  // Derive unique aisles from records
  const aisles = useMemo(() => {
    const set = new Set<string>();
    for (const r of flatRecords) {
      const aisle = aisleGroup(r.location_code);
      if (aisle) set.add(aisle);
    }
    return ['ALL', ...Array.from(set).sort()];
  }, [flatRecords]);

  const filtered = useMemo(() => {
    const q = search.trim().toUpperCase();
    const qc = searchComment.trim().toUpperCase();
    return flatRecords.filter((r) => {
      if (filterFlag === 'Empty') {
        if (r.drone_count !== 0) return false;
      } else if (filterFlag === 'NeedsReview') {
        if (!r.needs_review) return false;
      } else if (filterFlag === 'NoPhoto') {
        if (!r.missing_photo) return false;
      } else if (filterFlag === 'MissingQR') {
        // Cross-cutting: locations whose pallet QR could not be decoded.
        if (!(r.reasoning ?? '').includes('QR cannot be scanned')) return false;
      } else if (filterFlag === 'N/A') {
        // Needs-Review is its own bucket (matches the donut/chips), not N/A.
        if (r.customer_result !== 'N/A' || r.missing_photo || r.needs_review) return false;
      } else if (filterFlag !== 'ALL' && r.customer_result !== filterFlag) {
        return false;
      }
      if (filterAisle !== 'ALL') {
        const aisle = aisleGroup(r.location_code);
        if (aisle !== filterAisle) return false;
      }
      if (q && !r.location_code.toUpperCase().includes(q) && !(r.expected_sku ?? '').toUpperCase().includes(q)) return false;
      if (qc && !(r.comments ?? '').toUpperCase().includes(qc)) return false;
      return true;
    });
  }, [flatRecords, filterFlag, filterAisle, search, searchComment]);

  // Wrap record in a minimal LocationNode for LocationDetail
  function makeNode(r: ReconciliationRecord): LocationNode {
    const parts = r.location_code.split(/[-_\s]+/);
    return {
      location_code: r.location_code,
      aisle: aisleGroup(r.location_code),
      bay:   parts[1] ?? '',
      level: parts[2] ?? '',
      record: r,
    };
  }

  return (
    <div className="location-list">
      {/* Filter bar */}
      <div className="loc-filter-bar">
        <input
          type="search"
          className="loc-search-input"
          placeholder="🔍  Search location or SKU…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <input
          type="search"
          className="loc-search-input"
          placeholder="💬  Search comments…"
          value={searchComment}
          onChange={(e) => setSearchComment(e.target.value)}
        />
        <div className="loc-filter-row">
          <select
            className="loc-filter-select"
            value={filterAisle}
            onChange={(e) => setAisle(e.target.value)}
            aria-label="Filter by aisle"
          >
            {aisles.map((a) => (
              <option key={a} value={a}>{a === 'ALL' ? 'All aisles' : `Aisle ${a}`}</option>
            ))}
          </select>
          <select
            className="loc-filter-select"
            value={filterFlag}
            onChange={(e) => setFilter(e.target.value)}
            aria-label="Filter by status"
          >
            {RESULT_OPTIONS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
            <option value="NeedsReview">◑ Needs Review</option>
            <option value="NoPhoto">No Photo</option>
          </select>
        </div>
        <div className="loc-filter-count">
          {filtered.length} of {flatRecords.length} locations
        </div>
      </div>

      {/* Cards */}
      <div className="loc-card-list">
        {filtered.length === 0 ? (
          <div className="loc-list-empty">No locations match your filters.</div>
        ) : (
          filtered.map((r) => (
            <LocationCard
              key={r.id}
              record={r}
              selected={r.location_code === selectedCode}
              onClick={() => onSelect(makeNode(r))}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function CustomerDashboard() {
  const { user, signOut } = useAuth();
  const [searchParams] = useSearchParams();
  const urlMission = searchParams.get('mission');

  const [missions,        setMissions]        = useState<MissionSummary[]>([]);
  const [missionsLoading, setMissionsLoading] = useState(true);
  const [missionsError,   setMissionsError]   = useState<string | null>(null);

  const [selectedMissionId, setSelectedMissionId] = useState<string | null>(urlMission);
  const [missionData,       setMissionData]        = useState<MissionReconciliationData | null>(null);
  const [dataLoading,       setDataLoading]        = useState(false);
  const [dataError,         setDataError]          = useState<string | null>(null);
  const [exporting,         setExporting]          = useState(false);

  const [selectedLocation, setSelectedLocation] = useState<LocationNode | null>(null);
  const [showExec,      setShowExec]      = useState(false); // Executive Summary (manager+)
  const [showWorklist,  setShowWorklist]  = useState(false); // QR Correction Worklist (all users)
  const [showSkuWl,     setShowSkuWl]     = useState(false); // SKU Worklist — master Ti/Hi suspects (manager+)

  // ── Fetch missions on mount ───────────────────────────────────────────────
  useEffect(() => {
    setMissionsLoading(true);
    fetchMissions()
      .then((list) => {
        setMissions(list);
        setSelectedMissionId((current) => {
          if (current && list.some((mission) => mission.mission_id === current)) {
            return current;
          }

          const fallback = list[0]?.mission_id ?? null;
          if (fallback !== current) {
            setMissionData(null);
            setSelectedLocation(null);
          }
          return fallback;
        });
      })
      .catch((err) => setMissionsError(err.message ?? 'Failed to load missions.'))
      .finally(() => setMissionsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Authenticated Excel download ──────────────────────────────────────────
  const handleExportExcel = useCallback(async (missionId: string, title?: string | null) => {
    setExporting(true);
    try {
      const res = await apiClient.getRaw(`/api/inventory/missions/${missionId}/export.xlsx`);
      if (!res.ok) { alert(`Export failed (${res.status}).`); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(title ?? 'inventory').replace(/[^a-zA-Z0-9_-]/g, '_')}_report.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      alert('Could not download report — check your connection.');
    } finally {
      setExporting(false);
    }
  }, []);

  // ── Fetch records when mission changes ────────────────────────────────────
  useEffect(() => {
    if (!selectedMissionId) return;
    setDataLoading(true);
    setDataError(null);
    setSelectedLocation(null);
    fetchMissionRecords(selectedMissionId)
      .then((data) => {
        setMissionData(data);
        // Auto-select first location so the right pane is never blank on load.
        if (data.flat_records.length > 0) {
          const first = data.flat_records[0];
          const parts = first.location_code.split(/[-_\s]+/);
          setSelectedLocation({
            location_code: first.location_code,
            aisle: aisleGroup(first.location_code),
            bay:   parts[1] ?? '',
            level: parts[2] ?? '',
            record: first,
          });
        }
      })
      .catch((err) => setDataError(err.message ?? 'Failed to load reconciliation data.'))
      .finally(() => setDataLoading(false));
  }, [selectedMissionId]);

  const handleMissionSelect = useCallback((id: string) => {
    setSelectedMissionId(id);
    setMissionData(null);
  }, []);

  const handleLocationSelect = useCallback((loc: LocationNode) => {
    setSelectedLocation(loc);
  }, []);

  // Re-fetch records after a manager/admin Result override, preserving the
  // selected location so the detail pane + header counts reflect the new value.
  const reloadRecords = useCallback(async (updated?: ReconciliationRecord) => {
    // Reflect the just-saved record in the detail pane IMMEDIATELY from the PATCH
    // response, so the panel never shows a stale value while the refetch is in
    // flight (or if the refetch's location_code match below misses). The refetch
    // still refreshes the list cards + header donut counts.
    if (updated) {
      setSelectedLocation((prev) =>
        prev && prev.location_code === updated.location_code
          ? { ...prev, record: updated }
          : prev,
      );
    }
    if (!selectedMissionId) return;
    const data = await fetchMissionRecords(selectedMissionId);
    setMissionData(data);
    setSelectedLocation((prev) => {
      if (!prev) return prev;
      const fresh = data.flat_records.find((r) => r.location_code === prev.location_code);
      return fresh ? { ...prev, record: fresh } : prev;
    });
  }, [selectedMissionId]);

  // Report freeze — a frozen report is immutable and SOH-pinned. Managers,
  // admins and super-admins can freeze/unfreeze; edits are hidden when frozen.
  const frozenAt = missionData?.report_frozen_at ?? null;
  const isManagerRole =
    user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin';

  // Managers/admins/super-admins may override a location's Result + Comment —
  // but NOT while the report is frozen (server enforces this with 409s too).
  const canEditResults = isManagerRole && !frozenAt;

  // (Freeze/unfreeze actions moved off this header — see the report view.)

  // Per-customer report field set — records response carries the warehouse name;
  // fall back to the mission picker's summary while records are loading.
  const reportProfile = reportProfileFor(
    missionData?.warehouse_name
      ?? missions.find((m) => m.mission_id === selectedMissionId)?.warehouse_name,
  );

  // photo_id is now returned directly from the API on each record
  const photoId = selectedLocation?.record.photo_id ?? null;

  // ── Loading / error states ────────────────────────────────────────────────
  if (missionsLoading) {
    return (
      <div className="dash-loading">
        <Spinner size={48} label="Loading audit missions…" />
      </div>
    );
  }

  if (missionsError) {
    return (
      <div className="dash-error">
        <p>⚠ {missionsError}</p>
        <button className="btn btn--primary" onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="customer-dash">
      {/* ── Header ───────────────────────────────────────────────────────────── */}
      <header className="dash-header">
        <div className="dash-header-brand">
          <InfiniumMark variant="dark" height={34} />
        </div>

        <div className="dash-header-centre">
          <MissionPicker
            missions={missions}
            selectedId={selectedMissionId}
            onSelect={handleMissionSelect}
          />
          {missionData && <MissionSummaryBar records={missionData.flat_records} />}
        </div>

        {/* Header action buttons — hidden while any panel is open */}
        {missionData && !showExec && !showWorklist && !showSkuWl && (
          <div className="dash-header-actions">
            {(user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin') && (
              <button
                className="btn btn--primary btn--sm dash-exec-btn"
                title="Open the manager Executive Summary"
                onClick={() => setShowExec(true)}
              >
                📊 Exec Summary
              </button>
            )}
            <button
              className="btn btn--secondary btn--sm dash-exec-btn"
              title="View QR Correction Worklist with photos"
              onClick={() => setShowWorklist(true)}
            >
              🔧 QR Worklist
            </button>
            {(user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin') && (
              <button
                className="btn btn--secondary btn--sm dash-exec-btn"
                title="Suspected master Ti/Hi errors — records withheld (N/A) with photo + SOH evidence"
                onClick={() => setShowSkuWl(true)}
              >
                📦 SKU Worklist
              </button>
            )}
          </div>
        )}

        {/* Customer co-brand logos — top-right of the report header. */}
        <div className="dash-header-logos">
          <img src="/logos/dhl.png" alt="DHL" className="dash-header-logo" />
          <img src="/logos/celcomdigi.png" alt="CelcomDigi" className="dash-header-logo" />
        </div>

        <div className="dash-header-user">
          {user?.role === 'pilot' && (
            <Link to="/dashboard/flight" className="btn btn--ghost btn--sm">
              ← Flight
            </Link>
          )}
          {(user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin') && (
            <Link to="/dashboard/operations" className="btn btn--ghost btn--sm">
              ← Console
            </Link>
          )}
          {/* Freeze control removed from this header (2026-07-04, header was
              overcrowded) — freezing lives on the report view + API; a frozen
              mission still gates edits here via canEditResults/frozenAt. */}
          {selectedMissionId && frozenAt && (
            <span
              className="dash-frozen-badge"
              title={`Report frozen since ${new Date(frozenAt).toLocaleString()} — results are read-only and pinned to the SOH at freeze time.`}
            >
              🔒 Frozen
            </span>
          )}
          {selectedMissionId && (
            <button
              className="btn btn--primary btn--sm"
              title="Download full inventory report as Excel"
              disabled={exporting}
              onClick={() => {
                const found = missions.find(ms => ms.mission_id === selectedMissionId);
                handleExportExcel(selectedMissionId, found?.mission_title);
              }}
            >
              {exporting ? '⏳ Exporting…' : '↓ Export Excel'}
            </button>
          )}
          <div className="dash-user-info">
            <span className="dash-user-name">{user?.name || user?.email}</span>
            <span className="dash-user-warehouse">
              {user?.warehouse_id ? `Warehouse ${user.warehouse_id.slice(0, 8)}…` : 'All warehouses'}
            </span>
          </div>
          <button className="btn btn--ghost btn--sm" onClick={signOut}>Sign out</button>
        </div>
      </header>

      {/* ── Body ─────────────────────────────────────────────────────────────── */}
      {showSkuWl && missionData ? (
        <SkuWorklist
          warehouseId={missionData.warehouse_id}
          onBack={() => setShowSkuWl(false)}
        />
      ) : showWorklist && selectedMissionId ? (
        <QRCorrectionWorklist
          missionId={selectedMissionId}
          missionTitle={missions.find(m => m.mission_id === selectedMissionId)?.mission_title ?? ''}
          onBack={() => setShowWorklist(false)}
        />
      ) : showExec && missionData ? (
        <ExecutiveSummary
          records={missionData.flat_records}
          missionTitle={missions.find(m => m.mission_id === selectedMissionId)?.mission_title ?? ''}
          missionId={selectedMissionId ?? ''}
          onBack={() => setShowExec(false)}
        />
      ) : (
      <div className="dash-body">
        {/* Left pane: searchable card list */}
        <div className="dash-left-pane">
          {dataLoading ? (
            <div className="dash-pane-loading">
              <Spinner size={32} label="Loading locations…" />
            </div>
          ) : dataError ? (
            <div className="dash-pane-error"><p>⚠ {dataError}</p></div>
          ) : missionData ? (
            <>
              <ResultDonut records={missionData.flat_records} />
              <LocationList
                flatRecords={missionData.flat_records}
                selectedCode={selectedLocation?.location_code ?? null}
                onSelect={handleLocationSelect}
              />
            </>
          ) : (
            <div className="dash-pane-empty">Select a mission above to view locations.</div>
          )}
        </div>

        <div className="dash-divider" role="separator" />

        {/* Right pane: detail */}
        <div className="dash-right-pane">
          <LocationDetail
            location={selectedLocation}
            photoId={photoId}
            missionId={selectedMissionId}
            canEdit={canEditResults}
            isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
            onSaved={reloadRecords}
            profile={reportProfile}
          />
        </div>
      </div>
      )}
    </div>
  );
}
